/* @flow */
import Vue from 'vue'
import axios from 'axios'
import normalize from 'jsonapi-normalizer'
import oPath from 'object-path'
import query from 'query-string'
import isPresent from 'is-present'

import {REMOVE_SESSION} from './session'
import {computePaginateSortFilterParams, computedCollectionCount} from '@/utils/api'
import {NEW_NOTIFICATION, REPLACE_NOTIFICATION} from './notifications'
import type {JsonApiEntity} from '@/store'

export const REPLACE_GATEWAYS = 'REPLACE_GATEWAYS'
export const ADD_GATEWAYS = 'ADD_GATEWAYS'
export const MODIFY_GATEWAY = 'MODIFY_GATEWAY'
export const REMOVE_GATEWAY = 'REMOVE_GATEWAY'
export const SET_GATEWAYS_TOTAL = 'SET_GATEWAYS_TOTAL'

export const FETCH_GATEWAYS = 'FETCH_GATEWAYS'
export const FETCH_APPLICATION_NETWORK_GATEWAYS = 'FETCH_APPLICATION_NETWORK_GATEWAYS'
export const FETCH_GATEWAY = 'FETCH_GATEWAY'
export const CREATE_GATEWAY = 'CREATE_GATEWAY'
export const UPDATE_GATEWAY = 'UPDATE_GATEWAY'
export const DELETE_GATEWAY = 'DELETE_GATEWAY'

const initialState = () => ({
  entities: {},
  statistics: {
    total: null,
  },
})

export default {
  state: initialState(),
  getters: {
    gatewayDefaults: () => ({}),
    gateways: ({entities = {}}: Object) =>
      //$FlowFixMe
      Object.keys(entities).map(key => entities[key]),
    gatewaysKeyedByEui: (state: Object, getters: Object) => {
      const gateways = {}
      getters.gateways.forEach(device => {
        gateways[device.eui] = device
      })
      return gateways
    },
    gatewayByEui: (state: Object, getters: Object) => (eui: string) =>
      getters.gateways.find(device => device.eui === eui) || {},
    gatewaysByAppEui: (state: Object, getters: Object) => (appEui: string) => {
      return getters.gateways.filter(
        device =>
          Array.isArray(device.application_networks) &&
          device.application_networks.includes(appEui)
      )
    },
  },
  mutations: {
    RESET_STATE(state: Object) {
      Object.assign(state, initialState())
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities
    },
    [REPLACE_GATEWAYS](state: Object, {entities = {}}: Object) {
      state.entities = entities
    },
    [ADD_GATEWAYS](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      }
    },
    [MODIFY_GATEWAY](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      }
    },
    [REMOVE_GATEWAY](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id)
    },
    [SET_GATEWAYS_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total
    },
  },
  actions: {
    [FETCH_GATEWAYS]: async function({commit, dispatch}: Object, payload: Object = {}) {
      try {
        let pathstring = '/gateways'
        if (payload.context && payload.context.orgId) {
          pathstring = '/organizations/' + payload.context.orgId + pathstring
        }

        const {data} = await axios.get(pathstring, {
          params: computePaginateSortFilterParams(payload),
        })

        const {pagination} = data.meta
        commit(SET_GATEWAYS_TOTAL, {total: pagination.collection_count})
        commit(REPLACE_GATEWAYS, {entities: normalize(data).entities.gateways})

        return {
          success: true,
          // Return fetched data as flat array
          items: data.data.map(item => item.attributes),
          collectionCount: computedCollectionCount({
            endRow: payload.endRow,
            filteredCount: pagination.filtered_count,
            collectionCount: pagination.collection_count,
          }),
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load gateways ',
          detail: err.message,
          color: 'error',
        })
      }
      return {success: false}
    },
    [FETCH_APPLICATION_NETWORK_GATEWAYS]: async function(
      {commit, dispatch}: Object,
      payload: Object
    ) {
      try {
        const {data} = await axios.get(
          `/application_networks/${payload.context.appEui}/gateways`,
          {
            params: computePaginateSortFilterParams(payload),
          }
        )

        const gateways = normalize(data).entities.gateways

        // ADD APPLICATION NETWORK EUI TO GW DATA
        if (isPresent(gateways)) {
          Object.keys(gateways).map(key => {
            const gateway = gateways[key]
            if (!isPresent(gateway.application_networks)) {
              gateway.application_networks = []
            }
            if (!gateway.application_networks.includes(payload.context.appEui)) {
              gateway.application_networks.push(payload.context.appEui)
            }
          })
        }

        commit(ADD_GATEWAYS, {entities: gateways})

        const {pagination} = data.meta
        return {
          success: true,
          // Return fetched data as flat array
          items: data.data.map(item => item.attributes),
          collectionCount: computedCollectionCount({
            endRow: payload.endRow,
            filteredCount: pagination.filtered_count,
            collectionCount: pagination.collection_count,
          }),
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load gateways ',
          detail: err.message,
          color: 'error',
        })
      }
      return {success: false}
    },
    [FETCH_GATEWAY]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {data} = await axios.get(`/gateways/${payload.id}`)
        const {entities} = normalize(data)
        commit(ADD_GATEWAYS, {entities: entities.gateways})
        if( data.data.attributes.api_error_status) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Machine API Error at ' + data.data.attributes.api_error_timestamp,
            detail: data.data.attributes.api_error_message,
            color: 'error'
          })
        }
        return {success: true}
      } catch (err) {
        const status = oPath.get(err, 'response.status')
        if (status !== 404) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Failed to load gateway ',
            detail: err.message,
            color: 'error',
          })
        }
        return {success: false, status}
      }
    },
    [CREATE_GATEWAY]: async function({commit, dispatch}: Object, payload: JsonApiEntity) {
      try {
        const {data} = await axios.post('/gateways', {
          data: {
            type: 'gateway',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(ADD_GATEWAYS, {entities: entities.gateways})
        if (!payload.suppressNotifications) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Saved new gateway!',
            color: 'success',
          })
        }
        return data.data.attributes.eui
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to save gateway ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [UPDATE_GATEWAY]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: 'Saving...',
          color: 'secondary',
          undoable: true,
        })
      }

      try {
        const {data} = await axios.patch(`/gateways/${payload.id}`, {
          data: {
            type: 'gateway',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(MODIFY_GATEWAY, entities.gateways[payload.id])
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: 'Saved!',
            color: 'success',
            undoable: true,
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to update gateway ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [DELETE_GATEWAY]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: `Deleting gateway...`,
          color: 'secondary',
        })
      }

      try {
        await axios.delete(`/gateways/${payload.id}?${query.stringify(payload.options)}`)
        commit(REMOVE_GATEWAY, payload)
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: `Deleted gateway!`,
            color: 'success',
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Failed to delete gateway `,
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/gateways.js